import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { FiLogOut, FiMenu, FiX } from 'react-icons/fi';
import AdminOrders from '../components/admin/AdminOrders';
import AdminCustomers from '../components/admin/AdminCustomers';
import AdminStaff from '../components/admin/AdminStaff';
import AdminItems from '../components/admin/AdminItems';
import AdminMemberships from '../components/admin/AdminMemberships';

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  display: flex;
`;

const Sidebar = styled(motion.div)`
  width: 250px;
  background: #0f0f0f;
  border-right: 1px solid #333;
  padding: 20px;
  position: fixed;
  height: 100vh;
  overflow-y: auto;
  z-index: 100;

  @media (max-width: 768px) {
    width: 100%;
    height: auto;
    position: relative;
    border-right: none;
    border-bottom: 1px solid #333;
  }
`;

const SidebarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const Logo = styled.h2`
  color: #ff6b35;
  font-size: 20px;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #fff;
  font-size: 24px;
  cursor: pointer;
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`;

const NavMenu = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (max-width: 768px) {
    display: ${props => (props.$isOpen ? 'flex' : 'none')};
  }
`;

const NavItem = styled.button`
  padding: 12px 16px;
  background: ${props => (props.$active ? '#ff6b35' : 'transparent')};
  border: none;
  border-radius: 6px;
  color: ${props => (props.$active ? '#fff' : '#999')};
  text-align: left;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => (props.$active ? '#ff6b35' : '#1a1a1a')};
    color: #fff;
  }
`;

const MainContent = styled.div`
  flex: 1;
  margin-left: 250px;
  padding: 40px;

  @media (max-width: 768px) {
    margin-left: 0;
    padding: 20px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const Title = styled.h1`
  color: #fff;
  font-size: 32px;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const MenuToggle = styled.button`
  background: none;
  border: none;
  color: #fff;
  font-size: 24px;
  cursor: pointer;
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`;

const LogoutButton = styled.button`
  padding: 10px 20px;
  background: #ff6b35;
  border: none;
  border-radius: 6px;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: #ff5722;
    transform: translateY(-2px);
  }
`;

const AdminDashboard = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      console.error('Error logging out:', err);
    }
  };

  const tabs = [
    { id: 'orders', label: 'Orders' },
    { id: 'customers', label: 'Customers' },
    { id: 'memberships', label: 'Memberships' },
    { id: 'items', label: 'Items' },
    { id: 'staff', label: 'Staff' },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'orders':
        return <AdminOrders />;
      case 'customers':
        return <AdminCustomers />;
      case 'memberships':
        return <AdminMemberships />;
      case 'items':
        return <AdminItems />;
      case 'staff':
        return <AdminStaff />;
      default:
        return <AdminOrders />;
    }
  };

  return (
    <DashboardContainer>
      <Sidebar
        initial={{ x: -250 }}
        animate={{ x: 0 }}
        transition={{ duration: 0.3 }}
      >
        <SidebarHeader>
          <Logo>Admin Panel</Logo>
          <CloseButton onClick={() => setSidebarOpen(false)}>
            <FiX />
          </CloseButton>
        </SidebarHeader>

        <NavMenu $isOpen={sidebarOpen}>
          {tabs.map((tab) => (
            <NavItem
              key={tab.id}
              $active={activeTab === tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setSidebarOpen(false);
              }}
            >
              {tab.label}
            </NavItem>
          ))}
        </NavMenu>
      </Sidebar>

      <MainContent>
        <Header>
          <div>
            <MenuToggle onClick={() => setSidebarOpen(!sidebarOpen)}>
              <FiMenu />
            </MenuToggle>
            <Title>
              {tabs.find(t => t.id === activeTab)?.label || 'Dashboard'}
            </Title>
          </div>
          <HeaderActions>
            <LogoutButton onClick={handleLogout}>
              <FiLogOut /> Logout
            </LogoutButton>
          </HeaderActions>
        </Header>

        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          {renderContent()}
        </motion.div>
      </MainContent>
    </DashboardContainer>
  );
};

export default AdminDashboard;
